import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/*
  Reusable carousel with drag, navigation arrows, and auto-scroll
  Props:
  - items: array of data items
  - renderItem: (item, index) => ReactNode (render function for each card content)
  - itemWidth: number (px) fixed width for each slide (default 360)
  - gap: number (px) horizontal gap between slides (default 24)
  - speedSeconds: number animation duration in seconds (default 30)
  - pauseOnHover: boolean pause animation on hover (default true)
  - className: string additional classes for the outer wrapper
*/

const InfiniteCarousel = ({
  items = [],
  renderItem,
  itemWidth = 360,
  gap = 24,
  speedSeconds = 30,
  pauseOnHover = true,
  className = '',
}) => {
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true);
  
  // Triple the items for smoother infinite scroll
  const tripled = [...items, ...items, ...items];
  
  // Smooth auto-scroll effect
  useEffect(() => {
    if (!autoScroll || !scrollRef.current) return;
    
    const scrollElement = scrollRef.current;
    const scrollWidth = scrollElement.scrollWidth / 3; // One set width
    
    const scroll = () => {
      if (scrollElement.scrollLeft >= scrollWidth) {
        scrollElement.scrollLeft = 0;
      } else {
        scrollElement.scrollLeft += 0.5; // Smooth continuous scroll
      }
    };
    
    const interval = setInterval(scroll, 10); // Run every 10ms for smooth animation
    return () => clearInterval(interval);
  }, [autoScroll, speedSeconds]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setAutoScroll(false);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => setAutoScroll(true), 2000); // Resume auto-scroll after 2s
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      setTimeout(() => setAutoScroll(true), 2000);
    }
  };

  const scrollTo = (direction) => {
    setAutoScroll(false);
    const scrollAmount = (itemWidth + gap) * 1; // Scroll one item
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
    setTimeout(() => setAutoScroll(true), 3000); // Resume auto-scroll after 3s
  };

  return (
    <div className={`relative mx-auto ${className}`}>
      {/* Left Arrow */}
      <button
        onClick={() => scrollTo('left')}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-[#10dfb2] text-white rounded-full p-3 shadow-lg transition-all duration-300 hover:scale-110"
        aria-label="Previous"
      >
        <ChevronLeft size={24} />
      </button>

      {/* Carousel Container */}
      <div
        ref={scrollRef}
        className={`overflow-x-scroll scrollbar-hide ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <div
          className="flex"
          style={{
            columnGap: `${gap}px`,
            width: 'max-content',
          }}
        >
          {tripled.map((item, idx) => (
            <div
              key={`carousel-item-${idx}`}
              className="flex-shrink-0"
              style={{ width: `${itemWidth}px` }}
            >
              {renderItem(item, idx)}
            </div>
          ))}
        </div>
      </div>

      {/* Right Arrow */}
      <button
        onClick={() => scrollTo('right')}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-[#10dfb2] text-white rounded-full p-3 shadow-lg transition-all duration-300 hover:scale-110"
        aria-label="Next"
      >
        <ChevronRight size={24} />
      </button>
    </div>
  );
};

export default InfiniteCarousel;
